/* eslint-disable react/jsx-boolean-value */
/* eslint-disable react/react-in-jsx-scope */
import { useState, useEffect, useMemo } from 'react'
import { observer } from 'mobx-react'
import { Helmet } from 'react-helmet'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Spinner } from 'grommet'

import { BrandI, Guest } from '../../types-project/Client'
import userR from '../../mobx/user'

type SortKey = 'recent' | 'az' | 'za' | 'unread'

// Tri "recent" (defaut) : on remonte d'abord les marques avec une campagne/deal
// en cours, puis on classe par date d'activite la plus recente. Les marques
// jamais activees finissent en bas -> on n'affiche jamais en tete une marque
// inactive depuis longtemps.
const sortBrands = (brands: BrandI[], key: SortKey): BrandI[] => {
  const arr = [...brands]
  const lastActivity = (b: BrandI) =>
    b.last_activity_at ? new Date(b.last_activity_at).getTime() : 0
  switch (key) {
    case 'az':
      return arr.sort((a, b) => (a.name ?? '').localeCompare(b.name ?? ''))
    case 'za':
      return arr.sort((a, b) => (b.name ?? '').localeCompare(a.name ?? ''))
    case 'unread':
      return arr.sort(
        (a, b) =>
          (b.messages_count ?? 0) - (a.messages_count ?? 0) ||
          lastActivity(b) - lastActivity(a)
      )
    case 'recent':
    default:
      return arr.sort((a, b) => {
        const aActive = (a.active_briefs_count ?? 0) > 0 ? 1 : 0
        const bActive = (b.active_briefs_count ?? 0) > 0 ? 1 : 0
        if (aActive !== bActive) return bActive - aActive
        return lastActivity(b) - lastActivity(a)
      })
  }
}

const formatRelative = (iso?: string | null): string | null => {
  if (!iso) return null
  const diff = Date.now() - new Date(iso).getTime()
  if (Number.isNaN(diff) || diff < 0) return null
  const day = 1000 * 60 * 60 * 24
  const days = Math.floor(diff / day)
  if (days <= 0) return "aujourd'hui"
  if (days === 1) return 'hier'
  if (days < 7) return `il y a ${days} j`
  if (days < 30) return `il y a ${Math.floor(days / 7)} sem.`
  if (days < 365) return `il y a ${Math.floor(days / 30)} mois`
  return `il y a ${Math.floor(days / 365)} an${days >= 730 ? 's' : ''}`
}

function Brands() {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const [search, setSearch] = useState('')
  const [sortKey, setSortKey] = useState<SortKey>('recent')

  useEffect(() => {
    userR.getBrands()
  }, [])

  const loading = userR.allBrands.loading || userR.me.loading

  const visibleBrands = useMemo(() => {
    const all = (userR.allBrands.data ?? []) as BrandI[]
    const term = search.trim().toLowerCase()
    const filtered = term
      ? all.filter((b) => (b.name ?? '').toLowerCase().includes(term))
      : all
    return sortBrands(filtered, sortKey)
  }, [userR.allBrands.data, search, sortKey])

  const selectBrand = (brand: BrandI) => {
    userR.setBrandSession(brand as BrandI<Guest>)
    // Cas "choix de marque depuis une offre createur" : on revient sur le profil
    // du createur une fois la marque selectionnee.
    const vogzterId = JSON.parse(sessionStorage.getItem('vogzter') ?? '{}')
    if (vogzterId && Object.keys(vogzterId).length > 0) {
      navigate(`/dashboard/profil-vogzter/${vogzterId}`)
      return
    }
    navigate(`/dashboard/brands/${brand._id}/briefs`)
  }

  return (
    <div className="w-full min-h-full bg-[#F6F7FB] pb-16">
      <Helmet>
        <meta charSet="utf-8" />
        <title>Vogz - Mes marques</title>
      </Helmet>

      <div className="w-full max-w-5xl mx-auto pt-8 px-4">
        <h1
          style={{
            color: '#2A42A7',
            fontFamily: 'Hurme-Black, sans-serif',
            fontSize: 28,
            marginBottom: 4,
          }}
        >
          {t('myprofil:brand-page.select-brand', 'Sélectionnez une marque')}
        </h1>
        <p style={{ color: '#9aa0bd', fontSize: 14, marginBottom: 24 }}>
          Cherchez une marque ou choisissez-la dans la liste pour accéder à ses
          campagnes.
        </p>

        {/* Barre recherche + tri */}
        <div className="flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between mb-7">
          <div
            className="flex items-center gap-2 bg-white rounded-full px-4 h-11 flex-1 sm:max-w-md"
            style={{ border: '1px solid #EEF0F7', boxShadow: '0 4px 16px rgba(42,66,167,.05)' }}
          >
            <i className="fal fa-search" style={{ color: '#9aa0bd' }} />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Rechercher une marque…"
              className="flex-1 bg-transparent outline-none text-sm"
              style={{ color: '#2A42A7' }}
            />
            {search && (
              <button type="button" onClick={() => setSearch('')} aria-label="Effacer">
                <i className="fa-solid fa-circle-xmark" style={{ color: '#c9cee3' }} />
              </button>
            )}
          </div>

          <div
            className="flex items-center gap-2 bg-white rounded-full px-4 h-11"
            style={{ border: '1px solid #EEF0F7' }}
          >
            <i className="fa-solid fa-arrow-down-wide-short" style={{ color: '#9aa0bd', fontSize: 13 }} />
            <select
              value={sortKey}
              onChange={(e) => setSortKey(e.target.value as SortKey)}
              className="bg-transparent outline-none text-sm cursor-pointer"
              style={{ color: '#2A42A7' }}
            >
              <option value="recent">Activité récente</option>
              <option value="az">Nom (A → Z)</option>
              <option value="za">Nom (Z → A)</option>
              <option value="unread">Messages non lus</option>
            </select>
          </div>
        </div>

        {loading ? (
          <div className="flex w-full justify-center py-20">
            <Spinner />
          </div>
        ) : (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(168px, 1fr))',
              gap: 18,
            }}
          >
            {visibleBrands.map((brand) => {
              const isActive = (brand.active_briefs_count ?? 0) > 0
              const unread = brand.messages_count ?? 0
              const relative = formatRelative(brand.last_activity_at)
              return (
                <button
                  key={brand._id}
                  type="button"
                  onClick={() => selectBrand(brand)}
                  className="relative flex flex-col items-center justify-start"
                  style={{
                    padding: '26px 16px 18px',
                    minHeight: 184,
                    gap: 12,
                    borderRadius: 18,
                    border: '1px solid #EEF0F7',
                    background: '#fff',
                    boxShadow: '0 6px 24px rgba(42,66,167,.07)',
                    cursor: 'pointer',
                    transition: 'transform .15s ease, box-shadow .15s ease',
                    textAlign: 'center',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-4px)'
                    e.currentTarget.style.boxShadow = '0 12px 32px rgba(97,90,246,.18)'
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)'
                    e.currentTarget.style.boxShadow = '0 6px 24px rgba(42,66,167,.07)'
                  }}
                >
                  {/* Crayon edition */}
                  <span
                    role="button"
                    tabIndex={0}
                    aria-label="Éditer la marque"
                    onClick={(e) => {
                      e.stopPropagation()
                      navigate(`/dashboard/profile/brand?brand=${brand._id}`)
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.stopPropagation()
                        navigate(`/dashboard/profile/brand?brand=${brand._id}`)
                      }
                    }}
                    className="absolute top-2.5 right-2.5 flex items-center justify-center"
                    style={{ width: 28, height: 28, borderRadius: 8, color: '#b6bcd6' }}
                  >
                    <i className="fa-regular fa-pen-to-square" />
                  </span>

                  {/* Badge messages non lus */}
                  {unread > 0 && (
                    <span
                      className="absolute top-2.5 left-2.5 flex items-center justify-center"
                      style={{
                        minWidth: 22,
                        height: 22,
                        padding: '0 6px',
                        borderRadius: 11,
                        background: '#615AF6',
                        color: '#fff',
                        fontSize: 11,
                        fontWeight: 700,
                      }}
                    >
                      {unread > 99 ? '99+' : unread}
                    </span>
                  )}

                  {brand.logo ? (
                    <img
                      src={brand.logo}
                      alt={brand.name}
                      style={{ width: 70, height: 70, objectFit: 'contain', borderRadius: 16, flexShrink: 0 }}
                    />
                  ) : (
                    <div
                      style={{
                        width: 70,
                        height: 70,
                        borderRadius: 16,
                        background: '#E8ECFC',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#615AF6',
                        fontSize: 28,
                        fontWeight: 800,
                        flexShrink: 0,
                      }}
                    >
                      {(brand.name || '?').charAt(0).toUpperCase()}
                    </div>
                  )}

                  <span
                    style={{
                      color: '#2A42A7',
                      fontFamily: 'Hurme-Bold, sans-serif',
                      fontSize: 15,
                      lineHeight: 1.25,
                    }}
                  >
                    {brand.name}
                  </span>

                  {isActive ? (
                    <span
                      className="flex items-center gap-1.5"
                      style={{
                        fontSize: 11.5,
                        fontWeight: 700,
                        color: '#1f9d6b',
                        background: '#E5F7EF',
                        padding: '3px 9px',
                        borderRadius: 999,
                      }}
                    >
                      <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#1f9d6b' }} />
                      {brand.active_briefs_count} en cours
                    </span>
                  ) : (
                    <span style={{ fontSize: 11.5, color: '#aab0cb' }}>
                      {relative ? `Activité ${relative}` : 'Pas encore activée'}
                    </span>
                  )}
                </button>
              )
            })}

            {/* Carte ajout marque */}
            <button
              type="button"
              onClick={() => navigate('/dashboard/profile/brand?new=1')}
              className="flex flex-col items-center justify-center"
              style={{
                padding: '26px 16px',
                minHeight: 184,
                gap: 12,
                borderRadius: 18,
                border: '2px dashed #D7DBEC',
                background: 'transparent',
                cursor: 'pointer',
                transition: 'all .15s ease',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = '#615AF6'
                e.currentTarget.style.background = '#F6F7FE'
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = '#D7DBEC'
                e.currentTarget.style.background = 'transparent'
              }}
            >
              <div
                style={{
                  width: 56,
                  height: 56,
                  borderRadius: '50%',
                  background: '#E8ECFC',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <i className="fa-solid fa-plus" style={{ fontSize: 20, color: '#615AF6' }} />
              </div>
              <span style={{ fontFamily: 'Hurme-Bold, sans-serif', fontSize: 14, color: '#615AF6', textAlign: 'center' }}>
                {t('myprofil:brand-page.add-brand', 'Ajouter une marque')}
              </span>
            </button>
          </div>
        )}

        {!loading && visibleBrands.length === 0 && search.trim() && (
          <p className="text-center text-sm mt-8" style={{ color: '#9aa0bd' }}>
            Aucune marque ne correspond à «&nbsp;{search}&nbsp;».
          </p>
        )}
      </div>
    </div>
  )
}

export default observer(Brands)
